/**
 * Marketing Flex Table — the page's four calls.
 *
 * Facts and Excel go to `/reports/flex-table/*`. The remembered setup rides on
 * the existing saved-filters store under a reserved page namespace, as the
 * global facility override does (global-facility.service.ts): one row, one
 * JSON value of ids and keys, scoped server-side to the user and account.
 *
 * REFUSALS ARE ANSWERS, NOT FAILURES. A period with too much activity, or an
 * invalid one, comes back 422 in the house shape `{ error: { code, message } }`
 * with a plain-language message meant for the user (the code is not shown). Both calls turn that into a `FlexReportRefusal` carrying the message,
 * so the page can show it as-is; anything else is a failure the page words
 * itself. The Excel call asks for a Blob, so its refusal body arrives as a
 * Blob too and has to be read back as JSON here.
 */
import axios from 'axios';
import { apiClient, getErrorMessage } from './apiClient';
import { getSavedFilters, saveFilter } from './filters.service';
import type { FlexFacts } from '@/types/marketingFlexTable.types';
import type { FlexExcelPayload } from '@/pages/reports/flex/flexExport';

export const FLEX_SETUP_PAGE_URL = '__marketing_flex_table__';
const FLEX_SETUP_FILTER_TYPE = 'setup';
const FLEX_SETUP_VIEW_TYPE = '1';

/** Selected option values, as the pickers hold them (strings of ids). */
export interface FlexFilterSelection {
  facility: string[];
  tags: string[];
  patientType: string[];
}

export class FlexReportRefusal extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FlexReportRefusal';
  }
}

/** The client may prefix a status; the user should see only the sentence. */
function cleanMessage(raw: unknown): string | null {
  if (typeof raw !== 'string') return null;
  const message = raw.replace(/^\s*\[\d{3}\]\s*/, '').trim();
  return message === '' ? null : message;
}

async function blobText(blob: Blob): Promise<string> {
  if (typeof blob.text === 'function') return blob.text();
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result ?? ''));
    reader.onerror = () => reject(reader.error);
    reader.readAsText(blob);
  });
}

/** A 422 with a message becomes a refusal; everything else is rethrown untouched. */
async function asRefusal(error: unknown): Promise<unknown> {
  if (!axios.isAxiosError(error) || error.response?.status !== 422) return error;
  const body: unknown = error.response.data;
  let message: string | null;
  if (typeof Blob !== 'undefined' && body instanceof Blob) {
    // The house error shape, read back out of the Blob the Excel call asked for.
    try {
      const parsed = JSON.parse(await blobText(body)) as { error?: { message?: unknown } } | null;
      message = cleanMessage(parsed?.error?.message);
    } catch {
      return error;
    }
  } else {
    // `{ error: { code, message } }` — getErrorMessage reads it. The code is never shown.
    message = cleanMessage(getErrorMessage(error, ''));
  }
  return message ? new FlexReportRefusal(message) : error;
}

function filtersArray(filters: FlexFilterSelection): string | null {
  const toIds = (values: string[]) => values.map(Number).filter((n) => Number.isFinite(n));
  const out: Record<string, number[]> = {};
  if (filters.facility.length > 0) out.facility = toIds(filters.facility);
  if (filters.tags.length > 0) out.tags = toIds(filters.tags);
  if (filters.patientType.length > 0) out['patient-type'] = toIds(filters.patientType);
  return Object.keys(out).length > 0 ? JSON.stringify(out) : null;
}

export async function fetchFacts(
  period: { from: string; to: string },
  filters: FlexFilterSelection,
): Promise<FlexFacts> {
  const params: Record<string, string> = { dateFrom: period.from, dateTo: period.to };
  const encoded = filtersArray(filters);
  if (encoded) params.filtersArray = encoded;
  try {
    const { data } = await apiClient.get<{ data: FlexFacts }>('/reports/flex-table/facts', { params });
    return data.data;
  } catch (error) {
    throw await asRefusal(error);
  }
}

export async function exportExcel(payload: FlexExcelPayload & { fileName: string }): Promise<Blob> {
  try {
    const res = await apiClient.post<Blob>('/reports/flex-table/export', payload, { responseType: 'blob' });
    return res.data;
  } catch (error) {
    throw await asRefusal(error);
  }
}

/** The stored setup, parsed but unchecked — the caller validates it. Null when none is saved. */
export async function loadSetup(): Promise<unknown> {
  const saved = await getSavedFilters(FLEX_SETUP_PAGE_URL, FLEX_SETUP_VIEW_TYPE);
  const raw = saved?.filters?.[FLEX_SETUP_FILTER_TYPE];
  const value = Array.isArray(raw) ? raw[0] : raw;
  if (typeof value !== 'string' || value === '') return null;
  try {
    return JSON.parse(value) as unknown;
  } catch {
    return null;
  }
}

export async function saveSetup(setup: unknown): Promise<void> {
  await saveFilter({
    filterType: FLEX_SETUP_FILTER_TYPE,
    filterValue: JSON.stringify(setup),
    pageUrl: FLEX_SETUP_PAGE_URL,
    viewType: FLEX_SETUP_VIEW_TYPE,
  });
}
